"use client"

import { useState, type ReactNode } from "react"
import { Link, useLocation } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { useBrandConfig } from "@/hooks/useBrandConfig"
import { cn } from "@/lib/utils"
import { CreditMeter } from "@/components/usage/CreditMeter"
import type { MyUsage } from "@/services/usageService"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

/** 金の四芒星マーク（デザイン正本）。アプリ名の左に置く。 */
function BrandMark() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="shrink-0"
      style={{ filter: "drop-shadow(0 0 6px rgba(181,118,15,.55))" }}
    >
      <path
        d="M12 1 C12.8 7 17 11.2 23 12 C17 12.8 12.8 17 12 23 C11.2 17 7 12.8 1 12 C7 11.2 11.2 7 12 1 Z"
        fill="var(--gold)"
      />
    </svg>
  )
}

/**
 * チャット／ボード切り替えタブ。
 * 現在地は useLocation で判定する。<768px（モバイル）では非表示——モバイル用の
 * 下部タブは別担当が用意するため、ここでは重複導線を出さない。
 */
function SegmentNav() {
  const { pathname } = useLocation()
  const items: { label: string; to: string }[] = [
    { label: "チャット", to: "/" },
    { label: "ボード", to: "/board" },
  ]

  return (
    <nav className="hidden md:flex items-center gap-0.5 bg-white/10 rounded-lg p-[3px]">
      {items.map((item) => {
        const isActive = pathname === item.to
        return (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              "px-3 py-1 rounded-md text-xs transition-colors",
              isActive
                ? "bg-white/90 text-night font-semibold"
                : "text-white/70 hover:text-white"
            )}
          >
            {item.label}
          </Link>
        )
      })}
    </nav>
  )
}

/**
 * ユーザーアバターのメニュー（Logout を格納）。
 *
 * 未認証時は何も描画しない（現行 isAuthenticated ガードと同等）。Logout は
 * 旧 ChatHeader.tsx と同一文言の確認ダイアログを経てから signOut() を呼ぶ。
 */
function AvatarMenu() {
  const { isAuthenticated, user, signOut } = useAuth()
  const [confirmOpen, setConfirmOpen] = useState(false)

  if (!isAuthenticated) return null

  const email = user?.profile?.email ?? ""
  const initial = email ? email.charAt(0).toUpperCase() : "?"

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="ユーザーメニュー"
            className="flex items-center justify-center h-[30px] w-[30px] shrink-0 rounded-full bg-white/15 border border-white/25 text-white text-xs font-semibold hover:bg-white/25 transition-colors"
          >
            {initial}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {email && (
            <>
              <DropdownMenuLabel className="max-w-52 truncate">
                {email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onSelect={() => setConfirmOpen(true)}>
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* ログアウト確認ダイアログ（旧 ChatHeader.tsx と同一文言）。 */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ログアウトの確認</AlertDialogTitle>
            <AlertDialogDescription>
              ログアウトしますか？再度サインインが必要になります。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={() => signOut()}>確認</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/** AppHeader のプロパティ。 */
export interface AppHeaderProps {
  /** 左端に置く要素（Chat では SidebarTrigger）。Board 等では省略可。 */
  leading?: ReactNode
  /** ブランド名マークとは別に出すタイトル（チャット名／プロジェクト名等）。無ければ非表示。 */
  title?: string
  /** タイトルの補足行（プロジェクト名等）。title が無ければ表示しない。 */
  subtitle?: string
  /** 本人スコープの利用状況。null のとき CreditMeter は何も表示しない。 */
  usage: MyUsage | null
}

/**
 * Chat / Board 共通のヘッダー。
 *
 * 左からブランドマーク＋アプリ名、チャット/ボード切り替えタブ（md 以上のみ）、
 * 任意のタイトル表示。右に利用クレジット常時表示とユーザーアバター（Logout は
 * アバターメニュー内）を並べる。Board への組み込みは別担当が行うため、leading /
 * title / subtitle はすべて任意で、Chat 固有の値に依存しない。
 * @param leading 左端に置く要素（Chat の SidebarTrigger 等）。
 * @param title ブランド名マークとは別に出すタイトル。
 * @param subtitle タイトルの補足行。
 * @param usage 本人スコープの利用状況（CreditMeter へそのまま渡す）。
 * @returns 共通ヘッダー要素。
 */
export function AppHeader({ leading, title, subtitle, usage }: AppHeaderProps) {
  const brand = useBrandConfig()

  return (
    <header
      className="h-14 flex items-center justify-between gap-3 px-5 text-white"
      style={{
        background:
          "linear-gradient(120deg, var(--night-deep) 0%, var(--night) 55%, #21456F 100%)",
      }}
    >
      <div className="flex items-center gap-3 min-w-0">
        {leading}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <BrandMark />
          <span className="font-semibold text-[14.5px] whitespace-nowrap">
            {brand.appTitle}
          </span>
        </Link>
        <SegmentNav />
        {title && (
          <div className="min-w-0 pl-3 ml-1 border-l border-white/20 hidden sm:block">
            <p className="text-sm font-medium truncate leading-tight">{title}</p>
            {subtitle && (
              <p className="text-xs text-white/60 truncate leading-tight">{subtitle}</p>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <CreditMeter usage={usage} onDark />
        <AvatarMenu />
      </div>
    </header>
  )
}
